// src/lib/scan.js — scan engine (pure functions, no React)
//
// Phase 1 of the plan. Pages the PDS:
//   1. held blobs      — com.atproto.sync.listBlobs
//   2. collections     — com.atproto.repo.describeRepo
//   3. referenced blobs — com.atproto.repo.listRecords per collection + extractor
//   4. diff            — orphans = held − referenced; missing = referenced − held,
//                        cross-checked against com.atproto.repo.listMissingBlobs
//
// Fail-closed: `trustworthy` is false (and deletion must be disabled) if ANY
// enumeration step fails or pagination terminates early. Partial data must
// never reach the delete path.

import { extractBlobRefs } from './extract';

export const DEFAULT_LIST_BLOBS_LIMIT = 1000;
export const DEFAULT_LIST_RECORDS_LIMIT = 100;

/**
 * @typedef {Object} ScanResult
 * @property {boolean} trustworthy
 * @property {string[]} held            - blob CIDs returned by listBlobs (sorted)
 * @property {string[]} referenced      - blob CIDs found in records (sorted)
 * @property {string[]} orphans         - held − referenced (sorted)
 * @property {Array<{cid: string, refs: string[]}>} missing - referenced − held with record URIs
 * @property {Array<{nsid: string, records: number, blobRefs: number}>} collections
 * @property {Array<{step: string, message: string}>} errors
 * @property {number} scannedAt         - epoch ms
 */

/**
 * Run a full scan against a PDS.
 *
 * @param {Object}   opts
 * @param {Function} opts.listBlobs     async ({did, limit, cursor}) => {data:{cids:[],cursor}}
 * @param {Function} opts.describeRepo  async ({repo}) => {data:{collections:[]}}
 * @param {Function} opts.listRecords   async ({repo, collection, limit, cursor}) => {data:{records:[],cursor}}
 * @param {Function} opts.listMissingBlobs async ({limit, cursor}) => {data:{blobs:[],cursor}}
 *              NOTE: takes only limit/cursor (operates on authenticated repo)
 * @param {string}   opts.did
 * @param {Object}   [opts.onProgress]  optional {onHeld, onCollection, onRecord, onMissing} callbacks
 * @returns {Promise<ScanResult>}
 */
export async function runScan({
  listBlobs,
  describeRepo,
  listRecords,
  listMissingBlobs,
  did,
  onProgress = {},
}) {
  const errors = [];
  const collections = [];
  const referencedByCid = new Map(); // cid -> Set<recordUri>
  const heldSet = new Set();
  let trustworthy = true;

  const fail = (step, message) => {
    errors.push({ step, message });
    trustworthy = false;
  };

  // 1. Held blobs — page until no cursor
  try {
    let cursor;

    do {
      const res = await listBlobs({ did, limit: DEFAULT_LIST_BLOBS_LIMIT, cursor });
      const cids = res?.data?.cids;

      if (!Array.isArray(cids)) {
        fail('listBlobs', 'Response missing cids array');
        break;
      }

      for (const cid of cids) heldSet.add(cid);
      onProgress.onHeld?.(heldSet.size);
      cursor = res?.data?.cursor;

      if (cursor !== undefined && cursor !== null && cursor !== '') {
        // continue paging
      } else {
        cursor = undefined;
      }
    } while (cursor);
  } catch (err) {
    fail('listBlobs', `Failed: ${err?.message || err}`);
  }

  // 2. Collections
  let nsids = [];

  try {
    const res = await describeRepo({ repo: did });
    nsids = res?.data?.collections || [];

    if (!Array.isArray(nsids)) {
      fail('describeRepo', 'Response missing collections array');
      nsids = [];
    }
  } catch (err) {
    fail('describeRepo', `Failed: ${err?.message || err}`);
  }

  // 3. Referenced blobs — for each collection, page records, extract refs
  for (const nsid of nsids) {
    const col = { nsid, records: 0, blobRefs: 0 };
    collections.push(col);
    let cursor;
    let paging = true;

    while (paging && trustworthy) {
      try {
        const res = await listRecords({
          repo: did,
          collection: nsid,
          limit: DEFAULT_LIST_RECORDS_LIMIT,
          cursor,
        });

        const records = res?.data?.records;

        if (!Array.isArray(records)) {
          fail('listRecords', `Collection ${nsid}: response missing records array`);
          break;
        }

        for (const rec of records) {
          col.records += 1;
          const uri = rec?.uri;
          const refs = extractBlobRefs(rec?.value);

          for (const ref of refs) {
            if (!referencedByCid.has(ref.cid)) referencedByCid.set(ref.cid, new Set());

            if (uri) referencedByCid.get(ref.cid).add(uri);
            col.blobRefs += 1;
          }
        }

        onProgress.onRecord?.(col.records);
        const next = res?.data?.cursor;

        if (next !== undefined && next !== null && next !== '') {
          cursor = next;
        } else {
          paging = false;
        }
      } catch (err) {
        fail('listRecords', `Collection ${nsid}: ${err?.message || err}`);
        paging = false;
      }
    }

    // Emit the collection row only once the whole collection has finished
    // enumerating — not once per batch.
    onProgress.onCollection?.(nsid, col.records, col.blobRefs);
  }

  // 4. Diff
  const held = [...heldSet].sort();
  const referenced = [...referencedByCid.keys()].sort();
  const heldMinusReferenced = new Set(heldSet);

  for (const cid of referenced) heldMinusReferenced.delete(cid);
  const orphans = [...heldMinusReferenced].sort(); // orphans = held − referenced
  const missing = [];

  for (const [cid, refs] of referencedByCid) {
    if (!heldSet.has(cid)) missing.push({ cid, refs: [...refs] });
  }

  missing.sort((a, b) => a.cid.localeCompare(b.cid));

  // 5. Cross-check missing against listMissingBlobs (PDS's own accounting)
  // NOTE: listMissingBlobs takes ONLY limit/cursor — it operates on the
  // authenticated session's repo. Passing `repo` is an invalid parameter.
  const pdsMissing = new Set();

  try {
    let cursor;

    do {
      const res = await listMissingBlobs({ limit: 500, cursor });
      const blobs = res?.data?.blobs;

      if (Array.isArray(blobs)) {
        for (const b of blobs) {
          if (b?.cid) pdsMissing.add(b.cid);
        }
      }

      const next = res?.data?.cursor;

      if (next !== undefined && next !== null && next !== '') cursor = next;
      else cursor = undefined;
    } while (cursor);
  } catch (err) {
    fail('listMissingBlobs', `Failed: ${err?.message || err}`);
  }

  const computedMissing = new Set(missing.map((m) => m.cid));
  const pdsMissingArr = [...pdsMissing].sort();

  const agrees =
    computedMissing.size === pdsMissingArr.length &&
    pdsMissingArr.every((c) => computedMissing.has(c));

  if (!agrees) {
    fail(
      'listMissingBlobs',
      `Cross-check disagreement: computed ${computedMissing.size} missing, PDS reports ${pdsMissingArr.length}`
    );
  }

  return {
    trustworthy,
    held,
    referenced,
    orphans,
    missing,
    collections,
    errors,
    scannedAt: Date.now(),
  };
}

export default runScan;
